import { Link, useParams } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import SEO from "../components/SEO";
import Reveal from "../components/Reveal";
import Breadcrumbs from "../components/Breadcrumbs";
import FAQAccordion from "../components/FAQAccordion";
import CTASection from "../components/CTASection";
import NotFound from "./NotFound";
import { useRequestModal } from "../components/RequestModal";
import { BUSINESS, FRONTLOAD_SIZES, ROLLOFF_SIZES, SERVICE_AREAS } from "../data/site";
import { track } from "../lib/api";

export default function ServiceAreaCity() {
  const { slug } = useParams();
  const { openRequest } = useRequestModal();
  const area = SERVICE_AREAS.find((a) => a.slug === slug);
  if (!area) return <NotFound />;
  const nearby = area.nearby.map((s) => SERVICE_AREAS.find((a) => a.slug === s)).filter(Boolean);

  const cityFaqs = [
    {
      question: `Do you deliver dumpsters to ${area.name}?`,
      answer: `Yes. We deliver 20, 30, and 40 yard roll-off dumpsters throughout ${area.name}, and offer residential and commercial front-load service. Call (928) 713-2710 or request a dumpster online.`,
    },
    {
      question: `How long can I keep a roll-off dumpster in ${area.name}?`,
      answer: "As long as the job requires — there is no maximum rental period. Same-Day Swaps Available and Saturday Swaps Available for existing rental customers.",
    },
    {
      question: "What materials can go in the dumpster?",
      answer: "Not sure if your material can go in the dumpster? Give us a call before loading. Certain materials may require special handling or may not be accepted.",
    },
  ];

  return (
    <>
      <SEO
        title={`Dumpster Rental in ${area.name}, AZ`}
        description={`Roll-off dumpster rentals and front-load waste service in ${area.name}, AZ. Locally owned YWS delivers 20–40 yard dumpsters. Same-Day Swaps Available. Call (928) 713-2710.`}
        path={`/service-areas/${area.slug}`}
        schema={{
          "@context": "https://schema.org",
          "@type": "Service",
          serviceType: "Dumpster Rental",
          provider: { "@type": "LocalBusiness", name: BUSINESS.name, telephone: BUSINESS.phone },
          areaServed: area.name,
        }}
      />
      <section className="bg-ink text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-14">
          <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Service Areas", to: "/service-areas" }, { label: area.name }]} dark />
          <div className="mt-8 max-w-3xl">
            <h1 className="font-heading font-extrabold tracking-tight text-4xl sm:text-5xl leading-[1.05]">
              Dumpster Rental in <span className="text-sky-400">{area.name}, AZ</span>
            </h1>
            <p className="mt-5 text-lg text-slate-300 leading-relaxed">{area.local}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <button onClick={() => openRequest()} className="px-8 py-4 rounded-lg bg-sky-600 hover:bg-sky-500 font-heading font-semibold transition-all active:scale-[0.98]" data-testid="city-request">
                Request a Dumpster
              </button>
              <a href={BUSINESS.phoneHref} onClick={() => track("click_to_call", { location: `city_${area.slug}` })} className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg border-2 border-slate-600 hover:border-sky-400 font-heading font-semibold transition-all" data-testid="city-call">
                <Phone className="h-4 w-4" /> {BUSINESS.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white" data-testid="city-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <h2 className="font-heading font-bold text-2xl sm:text-3xl text-slate-900 tracking-tight">Services in {area.name}</h2>
          </Reveal>
          <div className="mt-8 grid sm:grid-cols-3 gap-5">
            {[
              { title: "Roll-Off Dumpster Rentals", text: `${ROLLOFF_SIZES.map((s) => s.label).join(", ")} — for construction, contractors & one-time projects. No maximum rental period.`, to: "/roll-off-dumpsters", cta: "Roll-Off Details" },
              { title: "Residential Front-Load", text: "Recurring waste service with 3–8 yard dumpsters and flexible pickup schedules.", to: "/front-load/residential", cta: "Residential Service" },
              { title: "Commercial Front-Load", text: "Flexible waste service for local businesses, sized to your volume.", to: "/front-load/commercial", cta: "Commercial Service" },
            ].map((s, i) => (
              <Reveal key={s.title} delay={i * 0.07}>
                <Link to={s.to} className={`group block rounded-xl p-6 h-full border transition-all hover:-translate-y-1 hover:shadow-lg ${i === 0 ? "border-sky-600 bg-sky-50/50" : "border-slate-200 bg-white"}`} data-testid={`city-service-${i}`}>
                  <h3 className="font-heading font-bold text-lg text-slate-900">{s.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.text}</p>
                  <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-heading font-semibold text-sky-700 group-hover:gap-2.5 transition-all">{s.cta} <ArrowRight className="h-4 w-4" /></span>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6">
              <h3 className="font-heading font-semibold text-slate-900 mb-3">Common projects in {area.name}</h3>
              <div className="flex flex-wrap gap-2">
                {area.projects.map((p) => (
                  <span key={p} className="px-3.5 py-1.5 rounded-full bg-white border border-slate-200 text-sm text-slate-600">{p}</span>
                ))}
              </div>
              <p className="mt-4 text-xs text-slate-500">Front-load sizes: {FRONTLOAD_SIZES.map((s) => s.label).join(", ")} · Same-Day Swaps Available · Saturday Swaps Available</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-14 bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FAQAccordion items={cityFaqs} heading={`${area.name} Dumpster FAQs`} />
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-xl text-slate-900">Also Serving Nearby</h2>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {nearby.map((n) => (
              <Link key={n.slug} to={`/service-areas/${n.slug}`} className="px-4 py-2 rounded-full border border-slate-200 text-sm font-medium text-slate-700 hover:border-sky-500 hover:text-sky-700 transition-colors" data-testid={`city-nearby-${n.slug}`}>
                {n.name}
              </Link>
            ))}
            <Link to="/service-areas" className="px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 transition-colors" data-testid="city-all-areas">
              All Service Areas
            </Link>
          </div>
        </div>
      </section>

      <CTASection title={`Need a Dumpster in ${area.name}?`} />
    </>
  );
}
